import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataProtectionNotice } from "@/components/DataProtectionNotice";
import { TermsLabel } from "@/components/TermsLabel";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const BirthdayReservationForm = () => {
  const { language } = useLanguage();
  const t = translations[language].birthdays.form;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define the form schema
  const formSchema = z.object({
    // Parent information
    parentName: z.string().min(2, { message: "Name is required" }).max(100),
    parentEmail: z.string().email({ message: "Valid email is required" }).max(255),
    parentPhone: z.string().min(9, { message: "Valid phone is required" }).max(20),
    
    // Child information
    childName: z.string().min(2, { message: "Child name is required" }).max(100),
    childAge: z.number().int().min(1).max(12),
    numberOfChildren: z.number().int().min(1).max(30),
    
    // Dates
    preferredDate: z.date({ required_error: "Preferred date is required" }),
    alternativeDate: z.date().optional(),
    
    // Plan and workshops
    selectedPlan: z.enum(['aTuAire', 'acompanada', 'experienciaCompleta'], {
      required_error: "Please select a plan"
    }),
    preferredWorkshops: z.array(z.string()).max(2),
    
    // Extras
    extras: z.object({
      miniDisco: z.boolean().default(false),
      adultSnacks: z.boolean().default(false),
      adultSnacksCount: z.number().int().min(0).optional(),
      cleaning: z.boolean().default(false),
      cake: z.boolean().default(false),
      additionalWorkshop: z.boolean().default(false)
    }),
    
    // Comments and terms
    comments: z.string().max(500).optional(),
    acceptTerms: z.boolean().refine(val => val, { message: "You must accept the terms" })
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parentName: "",
      parentEmail: "",
      parentPhone: "",
      childName: "",
      childAge: 5,
      numberOfChildren: 10,
      preferredWorkshops: [],
      extras: {
        miniDisco: false,
        adultSnacks: false,
        adultSnacksCount: 0,
        cleaning: false,
        cake: false,
        additionalWorkshop: false
      },
      comments: "",
      acceptTerms: false
    }
  });

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Format dates as ISO strings for the edge function
      const requestData = {
        ...values,
        preferredDate: values.preferredDate.toISOString(),
        alternativeDate: values.alternativeDate?.toISOString(),
        language, // Add current language
      };

      const { data, error } = await supabase.functions.invoke('send-birthday-reservation', {
        body: requestData,
      });

      if (error) {
        console.error("Error invoking edge function:", error);
        throw error;
      }

      console.log("Birthday reservation sent successfully:", data);
      toast.success(t.success);
      form.reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableWorkshops = translations[language].birthdays.workshops.items;

  return (
    <div className="bg-card p-6 md:p-8 rounded-2xl shadow-lg">
      <h3 className="text-2xl md:text-3xl font-light text-primary mb-6">{t.title}</h3>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          {/* Parent Contact Information */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-primary">{t.contactSection}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="parentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t.fields.parentName}</FormLabel>
                    <FormControl>
                      <Input placeholder={t.placeholders.parentName} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="parentEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t.fields.email}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t.placeholders.email} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="parentPhone"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t.fields.phone}</FormLabel>
                    <FormControl>
                      <Input placeholder={t.placeholders.phone} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* Birthday Child Information */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-primary">{t.birthdaySection}</h4>
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="childName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t.fields.childName}</FormLabel>
                    <FormControl>
                      <Input placeholder={t.placeholders.childName} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="childAge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t.fields.childAge}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1} 
                        max={12}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="numberOfChildren"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t.fields.numberOfChildren}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1} 
                        max={30}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* Preferences Section */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-primary">{t.preferencesSection}</h4>
            
            {/* Preferred Date */}
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              <FormField
                control={form.control}
                name="preferredDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t.fields.preferredDate}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>{t.placeholders.preferredDate}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Alternative Date */}
              <FormField
                control={form.control}
                name="alternativeDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t.fields.alternativeDate}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>{t.placeholders.alternativeDate}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* Selected Plan */}
            <FormField
              control={form.control}
              name="selectedPlan"
              render={({ field }) => (
                <FormItem className="space-y-3 mb-4">
                  <FormLabel>{t.fields.selectedPlan}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-2"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0 border rounded-lg p-3">
                        <FormControl>
                          <RadioGroupItem value="aTuAire" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer flex-1">
                          {translations[language].birthdays.plans.aTuAire.name}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0 border rounded-lg p-3">
                        <FormControl>
                          <RadioGroupItem value="acompanada" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer flex-1">
                          {translations[language].birthdays.plans.acompanada.name}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0 border border-primary rounded-lg p-3 bg-accent/5">
                        <FormControl>
                          <RadioGroupItem value="experienciaCompleta" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer flex-1">
                          {translations[language].birthdays.plans.experienciaCompleta.name}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Preferred Workshops */}
            <FormField
              control={form.control}
              name="preferredWorkshops"
              render={() => (
                <FormItem>
                  <FormLabel>{t.fields.preferredWorkshops}</FormLabel>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {availableWorkshops.map((workshop, index) => (
                      <FormField
                        key={index}
                        control={form.control}
                        name="preferredWorkshops"
                        render={({ field }) => {
                          return (
                            <FormItem
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(workshop)}
                                  disabled={field.value?.length >= 2 && !field.value?.includes(workshop)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, workshop])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== workshop
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal text-sm">
                                {workshop}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          {/* Extras Section */}
          <div>
            <h4 className="text-lg font-medium mb-4 text-primary">{t.extrasSection}</h4>
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="extras.miniDisco"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {translations[language].birthdays.extras.items[0].name} ({translations[language].birthdays.extras.items[0].price})
                    </FormLabel>
                  </FormItem>
                )}
              />
              
              <div className="flex flex-col gap-2">
                <FormField
                  control={form.control}
                  name="extras.adultSnacks"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {translations[language].birthdays.extras.items[1].name} ({translations[language].birthdays.extras.items[1].price})
                      </FormLabel>
                    </FormItem>
                  )}
                />
                
                {form.watch("extras.adultSnacks") && (
                  <FormField
                    control={form.control}
                    name="extras.adultSnacksCount"
                    render={({ field }) => (
                      <FormItem className="ml-6">
                        <FormLabel className="text-sm">{t.fields.adultSnacksCount}</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            min={0}
                            className="w-32"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </div>
              
              <FormField
                control={form.control}
                name="extras.cleaning"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {translations[language].birthdays.extras.items[2].name} ({translations[language].birthdays.extras.items[2].price})
                    </FormLabel>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="extras.cake"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {translations[language].birthdays.extras.items[3].name} ({translations[language].birthdays.extras.items[3].price})
                    </FormLabel>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="extras.additionalWorkshop"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {translations[language].birthdays.extras.items[4].name} ({translations[language].birthdays.extras.items[4].price})
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          {/* Comments */}
          <FormField
            control={form.control}
            name="comments"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t.fields.comments}</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder={t.placeholders.comments}
                    className="min-h-[100px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Terms and Conditions */}
          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="font-normal">
                    <TermsLabel />
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <DataProtectionNotice finalidad="cumpleanos" />

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-lg text-lg"
          >
            {isSubmitting ? t.submitting : t.submit}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default BirthdayReservationForm;
